import { RQAPICall, RQAPICallWithData } from 'store/APIClient';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ACTIVE_DASHBOARDS_QUERY_KEY } from 'routes/AllDashboards/hooks/getDashboardsList';
import { DashboardT } from 'business/definitions';
import { useDispatch } from 'store/hooks';

export const FAVOURITE_DASHBOARDS_QUERY_KEY = 'favourite_dashboards';

export const useGetFavouriteDashboards = () =>
    useQuery({
        queryKey: [FAVOURITE_DASHBOARDS_QUERY_KEY],

        queryFn: async () => {
            const response = await RQAPICallWithData<DashboardT[]>({
                endpoint: `explore/favourite_dashboards`,
                method: 'GET',
            });
            return response.content.data;
        },
    });

const toggleFavouriteDashboard = async (
    willBeFavourite: boolean,
    dashboardKey?: number,
) => {
    const response = await RQAPICall({
        endpoint: `explore/dashboard/${dashboardKey}/favourites`,
        method: willBeFavourite ? 'POST' : 'DELETE',
    });
    return response.content;
};

export const useToggleFavouriteDashboard = (
    dashboardKey?: number,
    onSuccess?: () => void,
) => {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ willBeFavourite }: { willBeFavourite: boolean }) =>
            toggleFavouriteDashboard(willBeFavourite, dashboardKey),

        onSuccess: (content, { willBeFavourite }) => {
            if (onSuccess) onSuccess();
            queryClient.setQueryData(
                [ACTIVE_DASHBOARDS_QUERY_KEY],
                (oldDashboards: DashboardT[] | undefined) =>
                    oldDashboards?.map((oldDashboard) =>
                        oldDashboard.key === dashboardKey
                            ? {
                                  ...oldDashboard,
                                  isUserFavourite: willBeFavourite,
                              }
                            : oldDashboard,
                    ),
            );
            dispatch.vision.notifyUser({
                message: content.message,
                snackType: 'success',
            });
        },
        meta: { useGlobalOnError: true },
    });
};
